const lib = require('./lib/lib');
const routes = require('./data/routes');

const GRANULAR_ROLES = {
    event_badge_printing_staff: true,
    event_meeting_planner: true,
};

module.exports = class AccessManager {
    constructor(roles = require('./data/roles'), permissions = require('./data/permissions')) {
        const processedPermissions = lib.expandPermissionGroups(permissions);

        this._roleMap = roles.reduce((map, roleObj) => {
            map[roleObj.name] = roleObj;
            return map;
        }, {});

        this._graph = lib.rolesToGraph(roles);
        lib.addAllRedundantEdges(this._graph);

        this._rolesPoliciesMatchers = lib.rolesToPermissionsMatchers(this._graph, processedPermissions);
    }

    /**
     * Determine if a user has a permission given her list of roles and a context
     * @param  {Array[Object]}  attributedRoles list of roles that the user has
     * @param  {string}  attributedRoles.role name of the role
     * @param  {string}  attributedRoles.attrCtx1 attribute 1 determining the context in which the user has the permission
     * @param  {[string]} attributedRoles.attrCtx2 attribute 2 determining the context in which the user has the permission
     * @param  {string}  permissionName name of the permission for which we want to know if the user has access
     * @param  {Object}  context context determining if the user has permission
     * @param  {string}  context.attrCtx1         context attribute 1
     * @param  {string}  context.attrCtx2         context attribute 2
     * @return {Boolean} true if the user has a role enabling the permission and the role is either a global role or
     * every attribute required for the role are both present in the attributed role and the context and are all equal
     * false otherwise
     */
    hasPermission(attributedRoles, permissionName, context) {
        /** @type {string[]} */
        const attributedRolesNames = new Set(attributedRoles.map(roleObj => roleObj.role));

        // all the roles that could potentially give this user the permission (regardless of context)
        const matchingRoles = this._getGlobMatchingRoles([...attributedRolesNames], permissionName);

        for (let roleDefinition of matchingRoles) {
            // optional step done for speedup (works since the filtering was already done)
            if (!roleDefinition.context) {
                return true;
            }

            for (let userRole of attributedRoles) {
                // further matching, looks into the context (event_id, org_id checks)
                if (lib.roleMatch(userRole, roleDefinition, context)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Determine if role is inherited by one specific user role.
     * @param  {Array[Object]}  userRole one of the user roles
     * @param  {string}  userRole.role name of the role
     * @param  {string}  userRole.attrCtx1 attribute 1 determining the context in which the user has the permission
     * @param  {[string]} userRole.attrCtx2 attribute 2 determining the context in which the user has the permission
     * @param  {Object}  requestedRole the requested role
     * @param  {string}  requestedRole.role  requested role name
     * @param  {string}  requestedRole.attrCtx1 context attribute 1
     * @param  {string}  requestedRole.attrCtx2 context attribute 2
     * @return {Boolean}
     */
    singleRoleInheritance(userRole, requestedRole) {

        const requiredContextList = this._graph[requestedRole.role].context;
        const allRoles = userRole.allRoles; //user role and all of inherited roles

        if (allRoles.includes(requestedRole.role)) {
            if (!requiredContextList || !this._graph[userRole.role].context ) {
                return true;  //either the required role or the user supported role is global and it is already included in list of user inherited roles
            }

            for (let attribute of requiredContextList) {   //only context roles left, and all attributes should match
                if (!requestedRole[attribute] || userRole[attribute] !== requestedRole[attribute]) {
                    return false;    //has higher global access or context attribute matches
                }
            }
            return true; //role is inherited and all context attributes match
        }
        return false;
    }

    /**
     * Determine if role is inherited by the supported user roles.
     * @param  {Array[Object]}  userRoles list of roles that the user has
     * @param  {string}  userRoles.role name of the role
     * @param  {string}  userRoles.attrCtx1 attribute 1 determining the context in which the user has the permission
     * @param  {[string]} userRoles.attrCtx2 attribute 2 determining the context in which the user has the permission
     * @param  {Object}  requestedRole the requested role
     * @param  {string}  requestedRole.role  requested role name
     * @param  {string}  requestedRole.attrCtx1 context attribute 1
     * @param  {string}  requestedRole.attrCtx2 context attribute 2
     * @return {Boolean} true if the user has a role enabling the permission and the role is either a global role or
     * every attribute required for the role are both present in the attributed role and the context and are all equal
     * false otherwise
     */
    isRoleInherited(userRoles, requestedRole) {
        if (!this._graph[requestedRole.role]) {
            return false;
        }

        return userRoles
            .map(roleObj => {
                const roleFromGraph = this._graph[roleObj.role];
                if (!roleFromGraph) {
                    //to be safe
                    return;

                }
                const inheritedRoles = roleFromGraph.inherits;
                return {
                    ...roleObj,
                    //list of role and all it inherits
                    allRoles: [roleObj.role].concat(inheritedRoles.map(role => role.name ))
                };
            })
            // if the user role that was checked has the inheritance, we can already return.
            // if not, we continue checking the next user role.
            .some(roleObj => roleObj && this.singleRoleInheritance(roleObj, requestedRole));
    }

    /**
     * Return whether the role array contains a role that has some custom route access
     * @param {Object[]} userRoles
     * @return {Boolean}
     */
    rolesHaveRoutes(userRoles) {
        for (const userRole of userRoles){
            if (userRole && userRole.role && routes[userRole.role]){
                return true;
            }
        }
        return false;
    }

    /**
     * Get additional array of routes that the role can access
     * @param {Object} userRole
     * @param {String} parentDocId
     * @return {String[]}
     */
    _getRoutesForRole(userRole, parentDocId){
        if (!userRole || !userRole.role || !routes[userRole.role]) {
            return [];
        }

        return routes[userRole.role](userRole.event_id, parentDocId);
    }

    /**
     * Get additional array of routes that the roles can access
     * @param {Object[]} userRoles
     * @param {String} parentDocId
     * @return {String[]}
     */
    getRoutesForRoles(userRoles, parentDocId){
        const routes = [];

        userRoles.forEach(userRole => {
            routes.push(...this._getRoutesForRole(userRole, parentDocId));
        });

        return routes;
    }

    /**
     * @param {Object[]} userRoles
     * @param {String} parentDocId
     * @return {String[]}
     */
    isRouteRestrictedRole(userRole) {
        return GRANULAR_ROLES[userRole.role];
    }

    /**
     * @param {Object[]} userRoles
     * @param {String} parentDocId
     * @return {String[]}
     */
    isGranularRole(userRole) {
        return GRANULAR_ROLES[userRole.role];
    }

    /**
     * @typedef {{name: string, context: Record<string, unknown>}} RoleDefinition
     *
     * @param {string[]} rolesToSearch
     * @param {string} permissionToVerify
     *
     * @returns {RoleDefinition[]}
     *
     * @private
     */
    _getGlobMatchingRoles(rolesToSearch, permissionToVerify) {
        /** @type {string[]} */
        const rolesMatchingPermissions = rolesToSearch.reduce((acc, roleName) => {
            const permissionMatchers = this._rolesPoliciesMatchers[roleName] || [];

            const roleHit = permissionMatchers.some(isMatch => isMatch(permissionToVerify));
            if (roleHit) {
                acc.push(roleName);
            }

            return acc;
        }, []);

        return rolesMatchingPermissions.map(roleName => this._roleMap[roleName]);
    }
};
